import React from 'react';
import { LucideIcon } from 'lucide-react';

export type IconButtonVariant = 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'ghost';
export type IconButtonSize = 'sm' | 'md';

export interface IconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Icon to display */
  icon: LucideIcon;
  /** Visual variant of the button */
  variant?: IconButtonVariant;
  /** Size of the button */
  size?: IconButtonSize;
  /** Loading state */
  loading?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * IconButton Atom Component
 * 
 * A button that displays only an icon, commonly used for actions in tables or toolbars.
 * 
 * @example
 * ```tsx
 * // Edit button
 * <IconButton icon={Edit} variant="primary" title="Edit user" />
 * 
 * // Delete button
 * <IconButton icon={Trash2} variant="error" title="Delete user" />
 * ```
 */
export const IconButton: React.FC<IconButtonProps> = ({
  icon: Icon,
  variant = 'ghost',
  size = 'sm',
  loading = false,
  className = '',
  disabled,
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center rounded transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';
  
  const variantClasses = {
    primary: 'text-primary-600 hover:text-primary-800 hover:bg-primary-50 focus:ring-primary-500',
    secondary: 'text-secondary-600 hover:text-secondary-800 hover:bg-secondary-50 focus:ring-secondary-500',
    success: 'text-green-600 hover:text-green-800 hover:bg-green-50 focus:ring-green-500',
    error: 'text-red-600 hover:text-red-800 hover:bg-red-50 focus:ring-red-500',
    warning: 'text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50 focus:ring-yellow-500',
    ghost: 'text-gray-600 hover:text-gray-800 hover:bg-gray-50 focus:ring-gray-500',
  };

  const sizeClasses = {
    sm: 'p-1 w-8 h-8',
    md: 'p-2 w-10 h-10',
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
  };

  const isDisabled = disabled || loading;

  const classes = [
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    isDisabled && 'opacity-50 cursor-not-allowed',
    className,
  ].filter(Boolean).join(' ');

  return (
    <button
      className={classes}
      disabled={isDisabled}
      {...props}
    >
      {loading ? (
        <svg className={`animate-spin ${iconSizes[size]}`} fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      ) : (
        <Icon className={iconSizes[size]} />
      )}
    </button>
  );
};
